import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from 'react-select';
import {
  Autocomplete,
  Alert,
  Avatar,
  Badge,
  Box,
  Pagination,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select as MUISelect,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { Link as RouterLink } from 'react-router-dom';
import { header } from '../components/header.encry';
import { generateBarcodeFromValue } from '../components/barcodeHelper';
import StoreService from '../components/StoreService';
import VariableService from '../components/VariableService';
import { formatOrderTicketDateTime } from '../utils/orderTicket';
import {
  buildCarrefourPluTicket,
  resolveStoredPlu,
} from '../utils/carrefourPlu';
import {
  fetchInventoryAskByStore,
  isAskActive,
} from '../services/inventoryAskService';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;
const IMAGE_BASE_URL = process.env.REACT_APP_DATA_PIC_PATH || '';

const formatCurrency = (value) => {
  const amount = Number(value) || 0;
  return `${amount.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} DHS`;
};

const resolveProductUnitPrice = (product) => {
  if (!product || typeof product !== 'object') {
    return 0;
  }

  const candidates = [
    product.price_ttc,
    product.regular_price,
    product.discounted_price,
    product.price,
    product.price_ht,
    product.product_price,
  ];

  for (const candidate of candidates) {
    const numeric = Number(candidate);
    if (Number.isFinite(numeric) && numeric >= 0) {
      return numeric;
    }
  }

  return 0;
};

const buildProductImageUrl = (source) => {
  if (!source) {
    return '';
  }

  const trimmedSource = String(source).trim();
  if (!trimmedSource) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmedSource)) {
    return trimmedSource;
  }

  if (!IMAGE_BASE_URL) {
    return trimmedSource;
  }

  const normalizedBase = IMAGE_BASE_URL.endsWith('/')
    ? IMAGE_BASE_URL
    : `${IMAGE_BASE_URL}/`;
  const normalizedSource = trimmedSource.startsWith('/')
    ? trimmedSource.slice(1)
    : trimmedSource;

  return `${normalizedBase}${normalizedSource}`;
};

const parseBooleanFlag = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return false;
    }

    return ['true', '1', 'yes', 'y', 'on'].includes(normalized);
  }

  return false;
};

const isComponentProductEntry = (product) => {
  if (!product || typeof product !== 'object') {
    return false;
  }

  return parseBooleanFlag(
    product.isComposant ??
      product.is_component ??
      product.isComponent ??
      product.component
  );
};

const normalizeStore = (store) => {
  if (!store) return null;
  const value = String(store._id ?? store.id ?? '');
  if (!value) return null;
  return {
    value,
    label: store.name || store.store_name || store.label || 'Magasin',
    raw: store,
  };
};

const normalizeProduct = (productData, balanceEntry) => {
  if (!productData && !balanceEntry) {
    return null;
  }

  const productId = String(
    balanceEntry?.product_id ??
      balanceEntry?.productId ??
      productData?._id ??
      productData?.id ??
      ''
  );

  if (!productId) {
    return null;
  }

  const price = Number(
    productData?.price_ttc ??
      productData?.regular_price ??
      productData?.discounted_price ??
      productData?.price ??
      balanceEntry?.product_price ??
      0
  );

  const quantity = Number(
    balanceEntry?.quantity ?? balanceEntry?.qty ?? balanceEntry?.stock ?? 0
  );

  const imageSource =
    productData?.url_product ||
    balanceEntry?.url_product ||
    balanceEntry?.product?.url_product ||
    productData?.imageUrl ||
    productData?.image_url ||
    productData?.main_image_url ||
    productData?.main_image ||
    productData?.image ||
    productData?.thumbnail ||
    productData?.thumb ||
    (Array.isArray(productData?.images) ? productData.images[0] : null) ||
    balanceEntry?.product_image ||
    balanceEntry?.image ||
    '';

  const isComponent =
    parseBooleanFlag(productData?.isComposant ?? productData?.is_component) ||
    parseBooleanFlag(balanceEntry?.isComposant ?? balanceEntry?.is_component) ||
    parseBooleanFlag(
      balanceEntry?.product?.isComposant ??
        balanceEntry?.product?.is_component
    );

  const details = Array.isArray(productData?.details)
    ? productData.details
    : Array.isArray(balanceEntry?.product?.details)
    ? balanceEntry.product.details
    : [];

  const categories = Array.isArray(productData?.categories)
    ? productData.categories
    : Array.isArray(balanceEntry?.product?.categories)
    ? balanceEntry.product.categories
    : [];

  const category =
    productData?.category || balanceEntry?.product?.category || null;

  const categoryName =
    productData?.categoryName ||
    productData?.category_name ||
    balanceEntry?.product?.categoryName ||
    balanceEntry?.product?.category_name ||
    '';

  const categoryNames = Array.isArray(productData?.categoryNames)
    ? productData.categoryNames
    : Array.isArray(balanceEntry?.product?.categoryNames)
    ? balanceEntry.product.categoryNames
    : [];

  return {
    id: productId,
    label:
      productData?.name ||
      productData?.label ||
      balanceEntry?.product_label ||
      balanceEntry?.product_name ||
      `Produit ${productId}`,
    barcode:
      productData?.barcode ||
      productData?.ref ||
      balanceEntry?.product_barcode ||
      balanceEntry?.barcode ||
      '',
    ref:
      productData?.ref ||
      productData?.reference ||
      balanceEntry?.product_ref ||
      balanceEntry?.product_barcode ||
      productId,
    price_ttc: Number.isFinite(price) ? price : 0,
    available_quantity: Number.isFinite(quantity) ? Math.max(quantity, 0) : 0,
    imageUrl: buildProductImageUrl(imageSource),
    isComponent,
    sku: productData?.sku || balanceEntry?.product?.sku || '',
    dolibarr_ref:
      productData?.dolibarr_ref || balanceEntry?.product?.dolibarr_ref || '',
    details,
    categories,
    category,
    categoryName,
    categoryNames,
  };
};

const parseFiniteNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const asTrimmedString = (value) => {
  if (value == null) {
    return '';
  }

  const stringValue = String(value).trim();
  return stringValue;
};

const coalesceNumber = (...candidates) => {
  for (const candidate of candidates) {
    const numericValue = parseFiniteNumber(candidate);
    if (numericValue != null) {
      return numericValue;
    }
  }

  return null;
};

const addNormalizedValueToSet = (set, value) => {
  const normalized = asTrimmedString(value);
  if (normalized) {
    set.add(normalized);
  }
};

const addMatchKey = (set, value) => {
  const normalized = asTrimmedString(value);
  if (normalized) {
    set.add(normalized.toLowerCase());
  }
};

const buildMatchKeySet = (...candidateGroups) => {
  const keys = new Set();

  candidateGroups.forEach((group) => {
    if (Array.isArray(group)) {
      group.forEach((value) => addMatchKey(keys, value));
      return;
    }

    addMatchKey(keys, group);
  });

  return keys;
};

const findTicketSummaryCandidate = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const visitedObjects = new WeakSet();
  const visitedArrays = new WeakSet();
  const queue = [payload];

  while (queue.length) {
    const current = queue.shift();

    if (!current) {
      continue;
    }

    if (Array.isArray(current)) {
      if (visitedArrays.has(current)) {
        continue;
      }

      visitedArrays.add(current);
      current.forEach((entry) => {
        if (entry && (typeof entry === 'object' || Array.isArray(entry))) {
          queue.push(entry);
        }
      });

      continue;
    }

    if (typeof current !== 'object') {
      continue;
    }

    if (visitedObjects.has(current)) {
      continue;
    }

    visitedObjects.add(current);

    if (current.result === false) {
      const errorMessage =
        typeof current?.response === 'string'
          ? current.response
          : current?.response?.message ||
            current?.message ||
            'Erreur lors de la génération du ticket.';
      const error = new Error(errorMessage);
      error.name = 'DolibarrTicketError';
      throw error;
    }

    const candidateArrays = [
      current.items,
      current.lines,
      current.details,
      current.products,
      current.tickets,
      current.ticket,
      current.data,
    ];

    for (const candidate of candidateArrays) {
      if (
        Array.isArray(candidate) &&
        candidate.some((item) => item && typeof item === 'object')
      ) {
        return { summary: current, items: candidate };
      }
    }

    Object.keys(current).forEach((key) => {
      const value = current[key];
      if (value && (typeof value === 'object' || Array.isArray(value))) {
        queue.push(value);
      }
    });
  }

  return null;
};

const normalizeTicketItems = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const quantityCandidate = coalesceNumber(
        item.qty,
        item.quantity,
        item.count,
        item.qte,
        item.nb,
        item.number
      );
      const quantity =
        quantityCandidate && quantityCandidate > 0 ? quantityCandidate : 1;

      const unitPriceCandidate = coalesceNumber(
        item.price,
        item.price_ttc,
        item.unit_price,
        item.subprice,
        item.pu_ht,
        item.pu_ttc
      );

      const totalCandidate = coalesceNumber(
        item.total,
        item.amount,
        item.total_ttc,
        item.total_ht,
        item.totalPrice,
        unitPriceCandidate != null ? unitPriceCandidate * quantity : null
      );

      const total = totalCandidate != null ? totalCandidate : 0;
      const unitPrice =
        unitPriceCandidate != null
          ? unitPriceCandidate
          : quantity
          ? total / quantity
          : total;

      const label =
        asTrimmedString(
          item.label ??
            item.name ??
            item.description ??
            item.desc ??
            item.product_label ??
            item.product_name
        ) || 'Article';

      const productId =
        asTrimmedString(
          item.product_id ??
            item.productId ??
            item.fk_product ??
            item.fk_product_id ??
            item.id ??
            item.product?.id
        ) || null;

      const ref =
        asTrimmedString(
          item.ref ??
            item.reference ??
            item.product_ref ??
            item.product_reference ??
            item.product?.ref
        ) || null;

      const barcode =
        asTrimmedString(
          item.barcode ??
            item.code_barre ??
            item.codebarre ??
            item.product?.barcode
        ) || null;

      const matchKeys = buildMatchKeySet(
        productId,
        ref,
        barcode,
        item.id,
        item.fk_product,
        item.fk_product_id,
        item.productId,
        item.product_id,
        item.product?.id,
        item.product?.ref,
        item.product?.barcode,
        item.identifiers,
        item.barcodes
      );

      addMatchKey(matchKeys, label);

      return {
        label,
        quantity,
        unitPrice,
        total,
        productId,
        ref,
        barcode,
        matchKeys,
      };
    })
    .filter(Boolean);
};

function StockUserHandle({ selectedTag } = {}) {
  const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
  const isCarrefour = roles.includes('carrefour');
  const isAdmin = roles.includes('admin');
  const isManager = roles.includes('manager');
  const canChooseStore = isAdmin || isManager;

  const defaultStoreId = sessionStorage.getItem('default_store_id') || '';

  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [store, setStore] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [showOnlyCart, setShowOnlyCart] = useState(false);
  const [selectedLines, setSelectedLines] = useState({});
  const [salesMode, setSalesMode] = useState('Client');
  const [inventoryReloadKey, setInventoryReloadKey] = useState(0);
  const [cartOpen, setCartOpen] = useState(false);
  const [transformDialogOpen, setTransformDialogOpen] = useState(false);
  const [transformFinalProductOption, setTransformFinalProductOption] =
    useState(null);
  const [transformFinalQuantity, setTransformFinalQuantity] = useState(1);
  const [transformComponentSelections, setTransformComponentSelections] =
    useState({});
  const [transformLoading, setTransformLoading] = useState(false);
  const [transformComponentFilter, setTransformComponentFilter] = useState('');
  const [page, setPage] = useState(1);
  const itemsPerPage = 0; // pagination removed, use infinite scroll
  const [visibleCount, setVisibleCount] = useState(30);
  const loadingRef = useRef(false);
  const [activeInventoryAsk, setActiveInventoryAsk] = useState(null);
  const [inventoryAskLoading, setInventoryAskLoading] = useState(false);
  const [carrefourPluCategories, setCarrefourPluCategories] = useState([]);
  const summaryColumnRef = useRef(null);
  const summaryCardRef = useRef(null);
  const [summaryPanelMetrics, setSummaryPanelMetrics] = useState({
    width: 0,
    height: 0,
  });
  const [isSummaryPinned, setIsSummaryPinned] = useState(false);
  const [summaryStartTop, setSummaryStartTop] = useState(0);

  const fetchCarrefourPluCategories = async () => {
    try {
      const variables = await VariableService.listVariables('plu');
      const list = Array.isArray(variables)
        ? variables
        : Array.isArray(variables?.data)
        ? variables.data
        : [];
      const pluVariable = list.find((entry) =>
        String(entry?.name || '').trim().toLowerCase().includes('plu carrefour')
      );
      const parsedCategories = pluVariable?.value
        ? JSON.parse(pluVariable.value)
        : [];

      return Array.isArray(parsedCategories) ? parsedCategories : [];
    } catch (error) {
      return [];
    }
  };

  useEffect(() => {
    window.dispatchEvent(new Event('pegasus:collapse-sidebar'));
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadCarrefourPluCategories = async () => {
      const categories = await fetchCarrefourPluCategories();
      if (mounted) {
        setCarrefourPluCategories(categories);
      }
    };

    loadCarrefourPluCategories();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const updateSummaryMetrics = () => {
      const nextWidth = summaryColumnRef.current?.offsetWidth || 0;
      const nextHeight = summaryCardRef.current?.offsetHeight || 0;
      const nextTop = summaryColumnRef.current
        ? summaryColumnRef.current.getBoundingClientRect().top + window.scrollY
        : 0;

      setSummaryPanelMetrics((previous) => {
        if (
          previous.width === nextWidth &&
          previous.height === nextHeight
        ) {
          return previous;
        }

        return {
          width: nextWidth,
          height: nextHeight,
        };
      });

      setSummaryStartTop(nextTop);
    };

    updateSummaryMetrics();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSummaryMetrics);
      return () => window.removeEventListener('resize', updateSummaryMetrics);
    }

    const observer = new ResizeObserver(() => {
      updateSummaryMetrics();
    });

    if (summaryColumnRef.current) {
      observer.observe(summaryColumnRef.current);
    }

    if (summaryCardRef.current) {
      observer.observe(summaryCardRef.current);
    }

    window.addEventListener('resize', updateSummaryMetrics);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateSummaryMetrics);
    };
  }, []);

  useEffect(() => {
    const handleSummaryPinState = () => {
      if (window.innerWidth < 1200) {
        setIsSummaryPinned(false);
        return;
      }

      const pinOffset = 24;
      setIsSummaryPinned(window.scrollY + pinOffset >= summaryStartTop);
    };

    handleSummaryPinState();
    window.addEventListener('scroll', handleSummaryPinState, { passive: true });
    window.addEventListener('resize', handleSummaryPinState);

    return () => {
      window.removeEventListener('scroll', handleSummaryPinState);
      window.removeEventListener('resize', handleSummaryPinState);
    };
  }, [summaryStartTop]);

  useEffect(() => {
    if (!canChooseStore) {
      return;
    }

    let mounted = true;
    const fetchStores = async () => {
      try {
        const data = await StoreService.fetchStores();
        const rawStores = data?.response || data || [];
        const normalized = rawStores
          .map((item) => normalizeStore(item))
          .filter((entry) => entry);

        if (!mounted) return;

        setStores(normalized);

        setSelectedStore((previous) => {
          if (previous) {
            const match = normalized.find(
              (storeOption) => storeOption.value === previous.value
            );
            if (match) {
              return match;
            }
          }

          const fallback =
            normalized.find(
              (storeOption) => storeOption.value === String(defaultStoreId)
            ) ||
            normalized[0] ||
            null;
          return fallback;
        });
      } catch (error) {
        console.error('Error fetching stores:', error);
        if (mounted) {
          setStores([]);
        }
        toast.error('Erreur lors du chargement des magasins');
      }
    };

    fetchStores();
    return () => {
      mounted = false;
    };
  }, [canChooseStore, defaultStoreId]);

  useEffect(() => {
    if (canChooseStore) {
      setStore(selectedStore?.raw || null);
      return;
    }

    if (!defaultStoreId) {
      setStore(null);
      return;
    }

    let mounted = true;
    const fetchStore = async () => {
      try {
        const data = await StoreService.fetchStore(defaultStoreId);
        if (mounted) {
          setStore(data?.response || data || null);
        }
      } catch (error) {
        if (mounted) {
          setStore(null);
        }
      }
    };

    fetchStore();
    return () => {
      mounted = false;
    };
  }, [canChooseStore, defaultStoreId, selectedStore]);

  const activeStoreId = useMemo(() => {
    if (isCarrefour) {
      return defaultStoreId || '';
    }
    if (canChooseStore) {
      return selectedStore?.value || '';
    }
    return defaultStoreId || '';
  }, [canChooseStore, defaultStoreId, isCarrefour, selectedStore]);

  useEffect(() => {
    if (!activeStoreId) {
      setAllProducts([]);
      return;
    }

    let mounted = true;
    const fetchAvailableProducts = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/products/available/${activeStoreId}`,
          header()
        );
        const payload = response?.data ?? {};
        const productsList =
          payload?.products ||
          payload?.response?.products ||
          payload?.response ||
          payload ||
          [];
        if (mounted) {
          setAllProducts(Array.isArray(productsList) ? productsList : []);
        }
      } catch (error) {
        console.error('Error fetching products list:', error);
        if (mounted) {
          setAllProducts([]);
        }
        toast.error('Erreur lors du chargement des produits');
      }
    };

    fetchAvailableProducts();
    return () => {
      mounted = false;
    };
  }, [activeStoreId]);

  useEffect(() => {
    if (!activeStoreId) {
      setProducts([]);
      return;
    }

    let mounted = true;
    const fetchInventoryProducts = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API_URL}/inventory/stock/store/${activeStoreId}/available`,
          {
            ...header(),
          }
        );

        const rawResponse = data?.response ?? data ?? {};
        const resolvedBalances = (() => {
          if (Array.isArray(rawResponse?.balances)) {
            return rawResponse.balances;
          }

          if (rawResponse?.balances && typeof rawResponse.balances === 'object') {
            return Object.values(rawResponse.balances);
          }

          if (Array.isArray(rawResponse)) {
            return rawResponse;
          }

          if (Array.isArray(data?.balances)) {
            return data.balances;
          }

          if (data?.balances && typeof data.balances === 'object') {
            return Object.values(data.balances);
          }

          return [];
        })();

        const entries = Array.isArray(resolvedBalances)
          ? resolvedBalances
          : Object.values(resolvedBalances || {});

        const productMap = new Map();
        allProducts.forEach((product) => {
          const id = String(product?._id ?? product?.id ?? '');
          if (id) {
            productMap.set(id, product);
          }
        });

        const normalizedProducts = entries
          .map((entry) => {
            const productId = String(
              entry?.product_id ??
                entry?.productId ??
                entry?.product?._id ??
                entry?.product?.id ??
                ''
            );

            const quantity = Number(
              entry?.quantity ??
                entry?.qty ??
                entry?.available_quantity ??
                entry?.stock ??
                entry?.product_quantity ??
                entry?.product?.quantity ??
                0
            );

            if (!productId || !Number.isFinite(quantity) || quantity <= 0) {
              return null;
            }

            const productData =
              productMap.get(productId) ||
              entry?.product ||
              entry?.product_data ||
              entry?.productData ||
              null;

            return normalizeProduct(productData, {
              ...entry,
              product_id: productId,
              quantity,
            });
          })
          .filter((item) => item && item.available_quantity > 0);

        if (mounted) {
          setProducts(normalizedProducts);
        }
      } catch (error) {
        console.error('Error fetching store inventory products:', error);
        if (mounted) {
          setProducts([]);
        }
        toast.error('Erreur lors du chargement du stock magasin');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchInventoryProducts();
    return () => {
      mounted = false;
    };
  }, [activeStoreId, allProducts, inventoryReloadKey]);

  useEffect(() => {
    if (!activeStoreId) {
      setActiveInventoryAsk(null);
      return;
    }

    let mounted = true;
    const loadInventoryAsk = async () => {
      setInventoryAskLoading(true);
      try {
        const ask = await fetchInventoryAskByStore(activeStoreId);
        if (mounted) {
          setActiveInventoryAsk(ask || null);
        }
      } catch (error) {
        if (mounted) {
          setActiveInventoryAsk(null);
        }
      } finally {
        if (mounted) {
          setInventoryAskLoading(false);
        }
      }
    };

    loadInventoryAsk();
    return () => {
      mounted = false;
    };
  }, [activeStoreId]);

  

  const resetTransformState = useCallback(() => {
    setTransformFinalProductOption(null);
    setTransformFinalQuantity(1);
    setTransformComponentSelections({});
  }, []);

  useEffect(() => {
    resetTransformState();
    setTransformDialogOpen(false);
  }, [activeStoreId, resetTransformState]);

  const storeLabel = useMemo(() => {
    if (store?.name || store?.store_name) {
      return store.name || store.store_name;
    }
    if (store?.label) {
      return store.label;
    }
    if (selectedStore?.label) {
      return selectedStore.label;
    }
    return '';
  }, [selectedStore, store]);

  const effectiveStoreIdForTransform =
    (store && (store._id || store.id)) ||
    selectedStore?.value ||
    activeStoreId ||
    '';

  const hasStoreForTransform = Boolean(effectiveStoreIdForTransform);

  const transformStoreDisplay = storeLabel || 'le magasin sélectionné';

  const hideAvailability = useMemo(
    () => !inventoryAskLoading && isAskActive(activeInventoryAsk),
    [activeInventoryAsk, inventoryAskLoading]
  );

  const inventoryAskOngoing = useMemo(() => {
    const status = activeInventoryAsk?.status || activeInventoryAsk?.Status;
    if (!status) return false;
    return String(status).toLowerCase() === 'ongoing';
  }, [activeInventoryAsk]);

  const showInventoryAskAlert = useMemo(
    () => inventoryAskOngoing && Boolean(activeStoreId) && !canChooseStore,
    [activeStoreId, canChooseStore, inventoryAskOngoing]
  );

  const toSearchableLower = (value) => {
    if (value === null || value === undefined) {
      return '';
    }
    try {
      return String(value).toLowerCase();
    } catch (error) {
      return '';
    }
  };

  const displayedProducts = useMemo(() => {
    let list = products;

    if (showOnlyCart) {
      list = list.filter((product) => selectedLines[product.id]);
    }

    if (filter) {
      const filterLower = filter.trim().toLowerCase();
      list = list.filter((product) => {
        const labelMatch = toSearchableLower(product.label).includes(
          filterLower
        );
        const barcodeMatch = toSearchableLower(product.barcode).includes(
          filterLower
        );
        const refMatch = toSearchableLower(product.ref).includes(filterLower);
        return labelMatch || barcodeMatch || refMatch;
      });
    }

    return list;
  }, [filter, products, selectedLines, showOnlyCart]);

  // Infinite scroll: load +30 items when scrolling near bottom
  useEffect(() => {
    const onScroll = () => {
      if (visibleCount >= displayedProducts.length) return;
      const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200;
      if (nearBottom && !loadingRef.current) {
        loadingRef.current = true;
        setVisibleCount(v => Math.min(displayedProducts.length, v + 30));
        setTimeout(() => { loadingRef.current = false; }, 600);
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [visibleCount, displayedProducts.length]);

  const finalProductOptions = useMemo(() => {
    if (!Array.isArray(allProducts)) {
      return [];
    }

    const optionMap = new Map();

    allProducts.forEach((productEntry) => {
      if (!productEntry || typeof productEntry !== 'object') {
        return;
      }

      const productId = asTrimmedString(
        productEntry._id ??
          productEntry.id ??
          productEntry.product_id ??
          productEntry.productId
      );

      if (!productId || optionMap.has(productId)) {
        return;
      }

      const label =
        asTrimmedString(productEntry.name) ||
        asTrimmedString(productEntry.label) ||
        asTrimmedString(productEntry.product_label) ||
        `Produit ${productId}`;

      optionMap.set(productId, {
        id: productId,
        label,
        barcode:
          asTrimmedString(productEntry.barcode) ||
          asTrimmedString(productEntry.ref) ||
          '',
        product: productEntry,
      });
    });

    return Array.from(optionMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })
    );
  }, [allProducts]);

  const componentProducts = useMemo(() => {
    if (!Array.isArray(allProducts)) {
      return [];
    }

    const inventoryMap = new Map();
    products.forEach((product) => {
      if (product?.id) {
        inventoryMap.set(String(product.id), product);
      }
    });

    const componentMap = new Map();

    allProducts.forEach((productEntry) => {
      if (!isComponentProductEntry(productEntry)) {
        return;
      }

      const productId = asTrimmedString(
        productEntry._id ??
          productEntry.id ??
          productEntry.product_id ??
          productEntry.productId
      );

      if (!productId || componentMap.has(productId)) {
        return;
      }

      const inventoryEntry = inventoryMap.get(productId);

      const normalized = normalizeProduct(productEntry, {
        product_id: productId,
        quantity:
          inventoryEntry?.available_quantity ?? inventoryEntry?.quantity ?? 0,
      });

      if (!normalized) {
        return;
      }

      const availableQuantityCandidate = Number(
        inventoryEntry?.available_quantity ??
          normalized.available_quantity ??
          0
      );

      const availableQuantity = Number.isFinite(availableQuantityCandidate)
        ? Math.max(availableQuantityCandidate, 0)
        : 0;

      if (availableQuantity <= 0) {
        return;
      }

      componentMap.set(productId, {
        ...normalized,
        available_quantity: availableQuantity,
      });
    });

    return Array.from(componentMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })
    );
  }, [allProducts, products]);

  const componentProductsMap = useMemo(() => {
    const map = new Map();
    componentProducts.forEach((product) => {
      map.set(product.id, product);
    });
    return map;
  }, [componentProducts]);

  useEffect(() => {
    setTransformComponentSelections((previous) => {
      let hasChanged = false;
      const next = {};

      Object.entries(previous).forEach(([productId, entry]) => {
        const product = componentProductsMap.get(productId);
        if (!product || !product.available_quantity) {
          hasChanged = true;
          return;
        }

        const currentQuantity = Number(entry?.quantity ?? 0);
        const normalizedQuantity = Math.max(
          1,
          Math.min(
            Math.floor(currentQuantity || 0) || 1,
            product.available_quantity
          )
        );

        if (
          normalizedQuantity !== currentQuantity ||
          entry.product !== product
        ) {
          hasChanged = true;
        }

        next[productId] = {
          product,
          quantity: normalizedQuantity,
        };
      });

      if (!hasChanged) {
        return previous;
      }

      return next;
    });
  }, [componentProductsMap]);

  const transformComponentList = useMemo(
    () => Object.values(transformComponentSelections),
    [transformComponentSelections]
  );

  const hasTransformComponents = transformComponentList.length > 0;

  const transformBillingItems = useMemo(
    () =>
      transformComponentList.map(({ product, quantity }) => {
        const unitPrice = resolveProductUnitPrice(product);
        const numericQuantity = Number(quantity) || 0;

        return {
          id: product?.id,
          label: product?.label,
          ref: product?.ref,
          barcode: product?.barcode,
          quantity: numericQuantity,
          unitPrice,
          total: unitPrice * numericQuantity,
        };
      }),
    [transformComponentList]
  );

  const transformBillingSubtotal = useMemo(
    () =>
      transformBillingItems.reduce((sum, item) => {
        if (!item) return sum;
        const total = Number(item.total) || 0;
        return sum + total;
      }, 0),
    [transformBillingItems]
  );

  const transformFinalQuantityNumber = useMemo(() => {
    const numeric = Number(transformFinalQuantity);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
  }, [transformFinalQuantity]);

  const transformUnitCost = useMemo(() => {
    if (!transformFinalQuantityNumber) {
      return 0;
    }

    return transformBillingSubtotal / transformFinalQuantityNumber;
  }, [transformBillingSubtotal, transformFinalQuantityNumber]);

  const allComponentQuantitiesValid = hasTransformComponents
    ? transformComponentList.every(({ product, quantity }) => {
        const numericQuantity = Number(quantity);
        if (!Number.isFinite(numericQuantity) || numericQuantity <= 0) {
          return false;
        }

        const available = Number(product?.available_quantity ?? 0);
        if (Number.isFinite(available) && available > 0) {
          return numericQuantity <= available;
        }

        return false;
      })
    : false;

  const transformSelectedComponentsCount = transformComponentList.length;

  const transformComponentsTotalQuantity = transformComponentList.reduce(
    (sum, entry) => sum + Number(entry?.quantity ?? 0),
    0
  );

  const isTransformFormValid =
    Boolean(transformFinalProductOption?.id) &&
    Number.isFinite(transformFinalQuantity) &&
    transformFinalQuantity > 0 &&
    allComponentQuantitiesValid;

  const handleSelectLine = (product, checked) => {
    setSelectedLines((previous) => {
      const next = { ...previous };
      if (checked) {
        if (!product.available_quantity || product.available_quantity <= 0) {
          return next;
        }
        const defaultQuantity = Math.min(
          product.available_quantity,
          previous[product.id]?.quantity || 1
        );
        next[product.id] = {
          product,
          quantity: defaultQuantity || 1,
          giftQuantity: previous[product.id]?.giftQuantity || 0,
          isGift: previous[product.id]?.isGift ?? false,
          unitPrice:
            previous[product.id]?.unitPrice ??
            (Math.round(product.price_ttc || 0) || 0),
        };
      } else {
        delete next[product.id];
      }
      return next;
    });
  };

  const handleQuantityChange = (productId, quantityValue) => {
    const parsedQuantity = Number(quantityValue);
    const nextQuantity = Number.isFinite(parsedQuantity)
      ? Math.max(0, parsedQuantity)
      : 0;

    setSelectedLines((previous) => {
      const currentLine = previous[productId];
      if (!currentLine) {
        return previous;
      }

      const product = currentLine.product;
      const available = Number(product?.available_quantity ?? 0);
      const giftQuantity = Number(currentLine.giftQuantity || 0);
      let cappedQuantity = nextQuantity;

      if (Number.isFinite(available) && available > 0) {
        const maxPaid = Math.max(available - giftQuantity, 0);
        cappedQuantity = Math.min(nextQuantity, maxPaid);
      }

      if (cappedQuantity <= 0 && giftQuantity <= 0) {
        const { [productId]: _removed, ...rest } = previous;
        return rest;
      }

      return {
        ...previous,
        [productId]: { ...currentLine, quantity: cappedQuantity },
      };
    });
  };

  const handleDecreaseQuantity = (productId) => {
    setSelectedLines((previous) => {
      const currentLine = previous[productId];
      if (!currentLine) {
        return previous;
      }

      const currentQuantity = Number(currentLine.quantity) || 0;
      const giftQuantity = Number(currentLine.giftQuantity || 0);

      if (currentQuantity <= 1) {
        const nextQuantity = Math.max(currentQuantity - 1, 0);

        if (nextQuantity <= 0 && giftQuantity <= 0) {
          const { [productId]: _removed, ...rest } = previous;
          return rest;
        }

        return {
          ...previous,
          [productId]: { ...currentLine, quantity: nextQuantity },
        };
      }

      return {
        ...previous,
        [productId]: { ...currentLine, quantity: currentQuantity - 1 },
      };
    });
  };

  const handleIncreaseQuantity = (productId) => {
    setSelectedLines((previous) => {
      const currentLine = previous[productId];
      if (!currentLine) {
        return previous;
      }

      const product = currentLine.product;
      const currentQuantity = Number(currentLine.quantity) || 0;
      const giftQuantity = Number(currentLine.giftQuantity || 0);
      const maxQuantity = Math.max(
        (product.available_quantity || Infinity) - giftQuantity,
        0
      );
      const nextQuantity = Math.min(currentQuantity + 1, maxQuantity);

      if (nextQuantity === currentQuantity) {
        return previous;
      }

      return {
        ...previous,
        [productId]: { ...currentLine, quantity: nextQuantity },
      };
    });
    
  };

const handleToggleGift = (productId, isGift) => {
  setSelectedLines((previous) => {
    const currentLine = previous[productId];
    if (!currentLine || !currentLine.product?.isComponent) {
      return previous;
    }

    const product = currentLine.product;
    const available = Number(product?.available_quantity ?? 0);

    const currentPaid = Number(currentLine.quantity) || 0;
    const currentGift = Number(currentLine.giftQuantity || 0);
    const totalSelected = currentPaid + currentGift || 0;

    // --- ENABLE GIFT MODE ---
    if (isGift) {
      // 🧠 Rule: if only 1 unit (stock or selected), don't split → everything is gift
      if (available <= 1 || totalSelected <= 1) {
        return {
          ...previous,
          [productId]: {
            ...currentLine,
            isGift: true,
            quantity: 0,            // no paid units
            giftQuantity: totalSelected > 0 ? totalSelected : 1, // 1 free unit
          },
        };
      }

      // Normal split logic when we have >1
      let giftQuantity = currentGift > 0 ? currentGift : 1;
      let paidQuantity = Math.max(currentPaid - 1, 0);

      if (Number.isFinite(available) && available > 0) {
        const total = paidQuantity + giftQuantity;
        if (total > available) {
          const overflow = total - available;
          if (paidQuantity >= overflow) {
            paidQuantity -= overflow;
          } else {
            const remainingOverflow = overflow - paidQuantity;
            paidQuantity = 0;
            giftQuantity = Math.max(giftQuantity - remainingOverflow, 1);
          }
        }
      }

      return {
        ...previous,
        [productId]: {
          ...currentLine,
          isGift: true,
          quantity: paidQuantity,   // paid part
          giftQuantity,             // gift part
        },
      };
    }

    // --- DISABLE GIFT MODE: merge both back into paid ---
    return {
      ...previous,
      [productId]: {
        ...currentLine,
        isGift: false,
        quantity: currentPaid + currentGift,
        giftQuantity: 0,
      },
    };
  });
};



  const handleGiftQuantityChange = (productId, value) => {
    const parsedQuantity = Number(value);
    const nextQuantity = Number.isFinite(parsedQuantity)
      ? Math.max(1, parsedQuantity)
      : 1;

    setSelectedLines((previous) => {
      const currentLine = previous[productId];
      if (!currentLine || !currentLine.isGift) {
        return previous;
      }

      const product = currentLine.product;
      const available = Number(product?.available_quantity ?? 0);
      const paidQuantity = Number(currentLine.quantity) || 0;
      let giftQuantity = nextQuantity;

      if (Number.isFinite(available) && available > 0) {
        const maxGift = Math.max(available - paidQuantity, 1);
        giftQuantity = Math.min(nextQuantity, maxGift);
      }

      return {
        ...previous,
        [productId]: { ...currentLine, giftQuantity },
      };
    });
  };

  const handleDecreaseGiftQuantity = (productId) => {
    setSelectedLines((previous) => {
      const currentLine = previous[productId];
      if (!currentLine || !currentLine.isGift) {
        return previous;
      }

      const giftQuantity = Number(currentLine.giftQuantity || 0);
      if (giftQuantity <= 1) {
        return previous;
      }

      return {
        ...previous,
        [productId]: { ...currentLine, giftQuantity: giftQuantity - 1 },
      };
    });
  };

  const handleIncreaseGiftQuantity = (productId) => {
    setSelectedLines((previous) => {
      const currentLine = previous[productId];
      if (!currentLine || !currentLine.isGift) {
        return previous;
      }

      const product = currentLine.product;
      const available = Number(product?.available_quantity ?? 0);
      const paidQuantity = Number(currentLine.quantity) || 0;
      const giftQuantity = Number(currentLine.giftQuantity || 0);
      let nextGift = giftQuantity + 1;

      if (Number.isFinite(available) && available > 0) {
        const maxGift = Math.max(available - paidQuantity, giftQuantity);
        nextGift = Math.min(nextGift, maxGift);
      }

      if (nextGift === giftQuantity) {
        return previous;
      }

      return {
        ...previous,
        [productId]: { ...currentLine, giftQuantity: nextGift },
      };
    });
  };

  const handleUnitPriceChange = (productId, value) => {
    const parsed = Number(value);
    const nextPrice = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;

    setSelectedLines((previous) => {
      const currentLine = previous[productId];
      if (!currentLine) {
        return previous;
      }

      return {
        ...previous,
        [productId]: { ...currentLine, unitPrice: nextPrice },
      };
    });
  };

  const handleEnsurePaidLine = (productId) => {
    setSelectedLines((previous) => {
      const currentLine = previous[productId];
      if (!currentLine) {
        return previous;
      }

      const available = Number(currentLine.product?.available_quantity ?? 0);
      const giftQuantity = Number(currentLine.giftQuantity || 0);
      const desiredPaidQuantity = Math.max(
        1,
        Number(currentLine.quantity || 0) || 1
      );

      const maxPaid = Number.isFinite(available)
        ? Math.max(available - giftQuantity, 0)
        : desiredPaidQuantity;

      if (maxPaid <= 0) {
        toast.warning('Stock insuffisant pour ajouter une version payante.');
        return previous;
      }

      const nextPaidQuantity = Math.min(desiredPaidQuantity, maxPaid);

      return {
        ...previous,
        [productId]: { ...currentLine, isGift: true, quantity: nextPaidQuantity },
      };
    });
  };

  const handleCreateOrder = async () => {
    const storeId = activeStoreId;
    if (!storeId) {
      toast.warning('Veuillez sélectionner un magasin.');
      return;
    }

    const storeName =
      storeLabel ||
      store?.name ||
      store?.store_name ||
      selectedStore?.label ||
      'Magasin Carrefour';

    const cartEntries = Object.values(selectedLines);
    if (!cartEntries.length) {
      toast.warning('Veuillez sélectionner au moins un produit.');
      return;
    }

    try {
      const normalizedCartEntries = [];
      const ticketRequestPayload = [];

      for (const { product, quantity, giftQuantity, isGift, unitPrice } of cartEntries) {
        const productId = product?.id;
        const payableQuantity = Number(quantity) || 0;
        const giftQuantityValue = Number(giftQuantity) || 0;
        const effectiveUnitPrice = Number.isFinite(Number(unitPrice))
          ? Number(unitPrice)
          : Math.round(product.price_ttc || 0);

        if (!productId) {
          toast.error('Produit invalide dans le panier.');
          return;
        }

        if (
          (!Number.isFinite(payableQuantity) || payableQuantity < 0) ||
          (!Number.isFinite(giftQuantityValue) || giftQuantityValue < 0) ||
          (payableQuantity <= 0 && giftQuantityValue <= 0)
        ) {
          toast.error('Quantité invalide pour le produit sélectionné.');
          return;
        }

        const fallbackBarcode =
          product.barcode || product.ref || product.id || productId;
        const productLookupKeys = Array.from(
          buildMatchKeySet(
            product?.barcode,
            product?.ref,
            product?.id,
            productId,
            fallbackBarcode,
            product?.label,
            product?.name,
            product?.product_label,
            product?.sku,
            product?.dolibarr_ref,
            Array.isArray(product?.details)
              ? product.details.flatMap((detail) => [
                  detail?.intern_code,
                  detail?.name,
                  detail?.type,
                ])
              : []
          )
        );

        normalizedCartEntries.push({
          product,
          productId,
          quantity: payableQuantity + giftQuantityValue,
          fallbackBarcode,
          isGift: Boolean(isGift && giftQuantityValue > 0),
          matchKeys: new Set(productLookupKeys),
        });

        if (payableQuantity > 0) {
          ticketRequestPayload.push({
            product_id: productId,
            quantity: payableQuantity,
            store_id: storeId,
            amount: effectiveUnitPrice,
            lookup_keys: productLookupKeys,
          });
        }

        if (giftQuantityValue > 0 && isGift) {
          ticketRequestPayload.push({
            product_id: productId,
            quantity: giftQuantityValue,
            store_id: storeId,
            isgift: true,
            amount: 0,
            lookup_keys: productLookupKeys,
          });
        }
      }

      const summaryBarcodes = new Set();
      const summaryItems = [];
      let ticketLines = [];
      let ticketSummaryInfo = null;
      let totalFromApiItems = 0;
      let countFromApiItems = 0;

      if (ticketRequestPayload.length) {
        let apiData;

        try {
          const baseTicketConfig = header({
            Accept: 'application/json, text/plain, */*',
          });

          const normalizedTicketHeaders = {
            ...(baseTicketConfig?.headers ?? {}),
          };

          if (
            normalizedTicketHeaders.ENCRYKEY &&
            !normalizedTicketHeaders.encrykey
          ) {
            normalizedTicketHeaders.encrykey =
              normalizedTicketHeaders.ENCRYKEY;
          }

          const ticketRequestConfig = {
            ...baseTicketConfig,
            headers: normalizedTicketHeaders,
            withCredentials: true,
          };

          const response = await axios.post(
            `${API_URL}/dolibarr/ticket`,
            ticketRequestPayload,
            ticketRequestConfig
          );

          apiData = response?.data;
        } catch (apiError) {
          console.error('Error generating Dolibarr ticket:', apiError);
          const message =
            apiError?.response?.data?.response ||
            apiError?.response?.data?.message ||
            apiError?.message ||
            'Erreur lors de la génération du ticket';
          toast.error(message);
          return;
        }

        try {
          const candidate = findTicketSummaryCandidate(apiData);

          if (candidate) {
            ticketSummaryInfo = candidate.summary || null;

            if (Array.isArray(candidate.summary?.barcodes)) {
              candidate.summary.barcodes.forEach((barcode) =>
                addNormalizedValueToSet(summaryBarcodes, barcode)
              );
            }

            const apiItems = normalizeTicketItems(candidate.items);
            apiItems.forEach((item) => {
              const normalizedBarcode = asTrimmedString(item.barcode) || '';

              ticketLines.push({
                description: item.label,
                price: item.total,
                quantity: item.quantity,
                barcodeSvg: normalizedBarcode
                  ? generateBarcodeFromValue(normalizedBarcode)
                  : '',
              });

              summaryItems.push({
                label: item.label,
                quantity: item.quantity,
                price: item.unitPrice,
                total: item.total,
                product_id: item.productId || undefined,
                ref: item.ref || undefined,
                barcode: item.barcode || undefined,
              });

              if (item.barcode) {
                addNormalizedValueToSet(summaryBarcodes, item.barcode);
              }

              totalFromApiItems += item.total;
              countFromApiItems += item.quantity;
            });
          }
        } catch (parsingError) {
          console.error('Error parsing Dolibarr ticket:', parsingError);
          toast.error(
            parsingError?.message ||
              'Erreur lors de la génération du ticket'
          );
          return;
        }
      }

      if (!ticketLines.length) {
        toast.error('Impossible de créer la commande.');
        return;
      }

      const linesTotal = ticketLines.reduce(
        (sum, line) => sum + (Number(line.price) || 0),
        0
      );
      const linesCount = summaryItems.reduce(
        (sum, item) => sum + Number(item.quantity || 0),
        0
      );

      const amountValue = coalesceNumber(
        ticketSummaryInfo?.amount,
        ticketSummaryInfo?.total,
        totalFromApiItems,
        linesTotal
      );

      const totalValue = coalesceNumber(
        ticketSummaryInfo?.total,
        ticketSummaryInfo?.amount,
        totalFromApiItems,
        linesTotal
      );

      const countValue = coalesceNumber(
        ticketSummaryInfo?.count,
        ticketSummaryInfo?.quantity,
        ticketSummaryInfo?.qty,
        ticketSummaryInfo?.nb,
        countFromApiItems,
        linesCount
      );

      const summaryBarcodeList = Array.from(summaryBarcodes).filter(Boolean);
      const directPluEntry = normalizedCartEntries.find((entry) =>
        resolveStoredPlu(entry?.product)
      );
      let resolvedPluCategories = carrefourPluCategories;

      if (!directPluEntry && !resolvedPluCategories.length) {
        resolvedPluCategories = await fetchCarrefourPluCategories();
        if (resolvedPluCategories.length) {
          setCarrefourPluCategories(resolvedPluCategories);
        }
      }

      const firstResolvedPluEntry = normalizedCartEntries.find((entry) =>
        resolveStoredPlu(entry?.product, resolvedPluCategories)
      );
      const firstResolvedPlu = firstResolvedPluEntry
        ? resolveStoredPlu(firstResolvedPluEntry.product, resolvedPluCategories)
        : '';

      const storedTicket = {
        storeName,
        lines: ticketLines,
        total: totalValue ?? linesTotal,
        orderDateTime: formatOrderTicketDateTime(),
      };

      const pluTicket = buildCarrefourPluTicket({
        plu: firstResolvedPlu,
        total: totalValue ?? linesTotal,
        label: firstResolvedPluEntry?.product?.label || 'PLU Carrefour',
        description: `Commande Carrefour - ${
          firstResolvedPluEntry?.product?.label || 'PLU'
        }`,
      });

      if (pluTicket) {
        storedTicket.pluTicket = pluTicket;
        storedTicket.ticketType = 'carrefour-plu';
      }

      if (
        summaryItems.length ||
        summaryBarcodeList.length ||
        amountValue != null ||
        totalValue != null ||
        countValue != null
      ) {
        storedTicket.ticketSummary = {
          amount: amountValue ?? undefined,
          total: totalValue ?? (amountValue ?? linesTotal),
          count: countValue ?? linesCount,
          items: summaryItems,
          barcodes: summaryBarcodeList,
        };

        if (ticketSummaryInfo) {
          const sanitizedSummary = {};
          ['amount', 'total', 'count', 'qty', 'quantity', 'nb'].forEach(
            (key) => {
              if (ticketSummaryInfo[key] != null) {
                sanitizedSummary[key] = ticketSummaryInfo[key];
              }
            }
          );

          if (Array.isArray(ticketSummaryInfo.barcodes)) {
            sanitizedSummary.barcodes = ticketSummaryInfo.barcodes
              .map(asTrimmedString)
              .filter(Boolean);
          }

          if (Object.keys(sanitizedSummary).length) {
            storedTicket.ticketSummary.apiSummary = sanitizedSummary;
          }
        }
      }

      localStorage.setItem('orderTicket', JSON.stringify(storedTicket));

      setSelectedLines({});
      setCartOpen(false);
      window.open('/order-ticket', '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error creating Carrefour order:', error);
      toast.error('Erreur lors de la création de la commande');
    }
  };

  const handleOpenTransformDialog = () => {
    if (!hasStoreForTransform) {
      toast.warning(
        'Veuillez sélectionner un magasin avant de transformer un produit.'
      );
      return;
    }

    resetTransformState();
    setTransformDialogOpen(true);
  };

  const handleCloseTransformDialog = () => {
    setTransformDialogOpen(false);
    resetTransformState();
  };

  const handleTransformFinalQuantityInput = (event) => {
    const rawValue = event?.target?.value;
    const parsed = Number(rawValue);

    if (!Number.isFinite(parsed) || parsed <= 0) {
      setTransformFinalQuantity(1);
      return;
    }

    setTransformFinalQuantity(Math.max(1, Math.floor(parsed)));
  };

  const handleToggleTransformComponent = (component, checked) => {
    if (!component?.id) {
      return;
    }

    if (
      checked &&
      (!component.available_quantity || component.available_quantity <= 0)
    ) {
      toast.warning(
        'Stock insuffisant pour ce composant dans le magasin sélectionné.'
      );
      return;
    }

    setTransformComponentSelections((previous) => {
      if (checked) {
        const currentQuantity = previous[component.id]?.quantity || 1;
        const nextQuantity = Math.max(
          1,
          Math.min(currentQuantity, component.available_quantity || 1)
        );

        return {
          ...previous,
          [component.id]: {
            product: component,
            quantity: nextQuantity,
          },
        };
      }

      if (!previous[component.id]) {
        return previous;
      }

      const { [component.id]: _removed, ...rest } = previous;
      return rest;
    });
  };

  const handleTransformComponentQuantityChange = (productId, nextValue) => {
    const parsed = Number(nextValue);

    setTransformComponentSelections((previous) => {
      const currentEntry = previous[productId];
      if (!currentEntry) {
        return previous;
      }

      const product =
        componentProductsMap.get(productId) || currentEntry.product;

      if (!product || !product.available_quantity) {
        const { [productId]: _removed, ...rest } = previous;
        return rest;
      }

      if (!Number.isFinite(parsed) || parsed <= 0) {
        const { [productId]: _removed, ...rest } = previous;
        return rest;
      }

      const maxQuantity = Number(product.available_quantity);
      const normalized = Math.min(
        Math.floor(parsed),
        Number.isFinite(maxQuantity) && maxQuantity > 0
          ? maxQuantity
          : Math.floor(parsed)
      );

      const finalQuantity = Math.max(1, normalized);

      if (
        finalQuantity === currentEntry.quantity &&
        currentEntry.product === product
      ) {
        return previous;
      }

      return {
        ...previous,
        [productId]: {
          product,
          quantity: finalQuantity,
        },
      };
    });
  };

  const handleIncreaseTransformComponentQuantity = (productId) => {
    setTransformComponentSelections((previous) => {
      const currentEntry = previous[productId];
      if (!currentEntry) {
        return previous;
      }

      const product =
        componentProductsMap.get(productId) || currentEntry.product;

      if (!product || !product.available_quantity) {
        const { [productId]: _removed, ...rest } = previous;
        return rest;
      }

      const maxQuantity = Number(product.available_quantity);
      if (
        Number.isFinite(maxQuantity) &&
        maxQuantity > 0 &&
        currentEntry.quantity >= maxQuantity
      ) {
        return previous;
      }

      return {
        ...previous,
        [productId]: {
          product,
          quantity: currentEntry.quantity + 1,
        },
      };
    });
  };

  const handleDecreaseTransformComponentQuantity = (productId) => {
    setTransformComponentSelections((previous) => {
      const currentEntry = previous[productId];
      if (!currentEntry) {
        return previous;
      }

      if (currentEntry.quantity <= 1) {
        const { [productId]: _removed, ...rest } = previous;
        return rest;
      }

      const product =
        componentProductsMap.get(productId) || currentEntry.product;

      return {
        ...previous,
        [productId]: {
          product,
          quantity: currentEntry.quantity - 1,
        },
      };
    });
  };

  const handleSubmitTransform = async () => {
    if (!hasStoreForTransform) {
      toast.warning('Veuillez sélectionner un magasin.');
      return;
    }

    if (!transformFinalProductOption?.id) {
      toast.warning('Veuillez choisir un produit final.');
      return;
    }

    if (!isTransformFormValid) {
      toast.warning('Veuillez vérifier les composants sélectionnés.');
      return;
    }

    const finalQuantity = Math.max(1, Math.floor(transformFinalQuantity || 1));

    const componentPayload = transformComponentList.map(
      ({ product, quantity }) => ({
        product_id: product.id,
        quantity: Math.max(1, Math.floor(Number(quantity) || 0)),
      })
    );

    const invalidComponents = componentPayload.filter(
      ({ product_id, quantity }) => {
        const product = componentProductsMap.get(product_id);
        if (!product || !product.available_quantity) {
          return true;
        }

        return quantity > product.available_quantity;
      }
    );

    if (invalidComponents.length) {
      toast.error('Stock insuffisant pour certains composants sélectionnés.');
      return;
    }

    const payload = {
      store_id: effectiveStoreIdForTransform,
      final_product: {
        product_id: transformFinalProductOption.id,
        quantity: finalQuantity,
      },
      components: componentPayload,
    };

    setTransformLoading(true);

    try {
      await axios.post(`${API_URL}/inventory/transform`, payload, header());
      toast.success('Transformation enregistrée avec succès.');
      setTransformDialogOpen(false);
      resetTransformState();
      setInventoryReloadKey((value) => value + 1);
    } catch (error) {
      console.error('Error creating inventory transform:', error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.response ||
        error?.message ||
        'Erreur lors de la transformation des produits.';
      toast.error(message);
    } finally {
      setTransformLoading(false);
    }
  };

  const cartDisplayItems = useMemo(() => {
    const lines = [];

    Object.values(selectedLines).forEach((line) => {
      const { product, quantity, giftQuantity, isGift, unitPrice } = line;
      const paidQuantity = Number(quantity) || 0;
      const giftLineQuantity = Number(giftQuantity) || 0;
      const hasGiftLine = isGift && giftLineQuantity > 0;
      const hasPaidLine = paidQuantity > 0;

      if (hasGiftLine) {
        lines.push({
          key: `${product.id}-gift`,
          product,
          quantity: giftLineQuantity,
          isGiftLine: true,
          unitPrice: 0,
          canToggleGift: true,
          giftToggleChecked: true,
        });
      }

      if (hasPaidLine) {
        lines.push({
          key: `${product.id}-paid`,
          product,
          quantity: paidQuantity,
          isGiftLine: false,
          unitPrice,
          canToggleGift: !isGift && Boolean(product?.isComponent),
          giftToggleChecked: false,
        });
      }
    });

    return lines.filter((line) => Number(line.quantity) > 0 || line.isGiftLine);
  }, [selectedLines]);

  const cartTotal = cartDisplayItems.reduce(
    (sum, { product, quantity, unitPrice, isGiftLine }) => {
      if (isGiftLine) {
        return sum;
      }

      const price = Number.isFinite(Number(unitPrice))
        ? Number(unitPrice)
        : Math.round(product.price_ttc || 0);
      return sum + price * Number(quantity || 0);
    },
    0
  );

  const totalCartQuantity = cartDisplayItems.reduce(
    (sum, { quantity }) => sum + Number(quantity || 0),
    0
  );

  const giftCartQuantity = cartDisplayItems.reduce(
    (sum, { product, quantity, isGiftLine }) => {
      if (product?.isComponent && isGiftLine) {
        return sum + Number(quantity || 0);
      }

      return sum;
    },
    0
  );

  const soldCartQuantity = Math.max(totalCartQuantity - giftCartQuantity, 0);

  const storeOptions = stores.map((option) => ({
    value: option.value,
    label: option.label,
  }));

  const selectedStoreOption = selectedStore
    ? { value: selectedStore.value, label: selectedStore.label }
    : null;

  const handleProductCardClick = (product) => {
    if (!product?.available_quantity) {
      return;
    }

    if (selectedLines[product.id]) {
      handleIncreaseQuantity(product.id);
      return;
    }

    handleSelectLine(product, true);
  };

  return (
    <Box sx={{ mt: '20px', p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" component="h1">
          Produits
          {storeLabel ? ` - ${storeLabel}` : ''}
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleOpenTransformDialog}
            disabled={transformLoading || !hasStoreForTransform}
            fullWidth
          >
            Transformer un produit
          </Button>
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'flex-end' }}
        sx={{ mb: 3 }}
      >
        {canChooseStore ? (
          <Box sx={{ width: '100%', maxWidth: 360 }}>
            <Typography variant="subtitle2" gutterBottom>
              Magasin
            </Typography>
            <Select
              id="storeSelect"
              options={storeOptions}
              value={selectedStoreOption}
              onChange={(option) => {
                if (!option) {
                  setSelectedStore(null);
                  return;
                }
                const match = stores.find(
                  (storeOption) => storeOption.value === option.value
                );
                setSelectedStore(match || null);
              }}
              placeholder="Choisissez un magasin"
              isClearable
            />
          </Box>
        ) : null}

        <FormControl sx={{ width: '100%', maxWidth: 260 }}>
          <InputLabel id="sales-mode-top-label">Mode de vente</InputLabel>
          <MUISelect
            labelId="sales-mode-top-label"
            label="Mode de vente"
            value={salesMode}
            onChange={(event) => setSalesMode(event.target.value)}
          >
            {['Client', 'Glovo', 'Bringo', 'Glovo Carrefour', 'Borne'].map(
              (mode) => (
                <MenuItem key={mode} value={mode}>
                  {mode}
                </MenuItem>
              )
            )}
          </MUISelect>
        </FormControl>
      </Stack>

      {showInventoryAskAlert && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              variant="outlined"
              size="small"
              component={RouterLink}
              to="/inventory-calc"
            >
              Ouvrir l'inventaire
            </Button>
          }
        >
          Un inventaire est en cours pour {storeLabel || 'votre magasin'}.
          Merci de saisir les quantités dans « Inventaire Calcul » avant de
          continuer les ventes.
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: 3 }}
      >
        <TextField
          label="Filtrer par nom, référence ou code-barres"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          fullWidth
        />
        <FormControlLabel
          control={
            <Switch
              checked={showOnlyCart}
              onChange={(event) => setShowOnlyCart(event.target.checked)}
              color="primary"
            />
          }
          label="Panier uniquement"
        />
      </Stack>

      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} lg={8}>

      {loading ? (
        <Box
          sx={{
            py: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : !activeStoreId ? (
        <Typography color="text.secondary">Aucun magasin sélectionné.</Typography>
      ) : displayedProducts.length > 0 ? (
        <>
          <Grid container spacing={3}>
          {(() => {
            const sliced = displayedProducts.slice(0, visibleCount);
            return sliced.map((product) => {
            const isSelected = Boolean(selectedLines[product.id]);
            const selectedQuantity =
              (selectedLines[product.id]?.quantity || 0) +
              (selectedLines[product.id]?.giftQuantity || 0);
            const hasImage = Boolean(product.imageUrl);
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <Card
                  onClick={() => handleProductCardClick(product)}
                  sx={{
                    width: '100%',
                    height: 255,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    cursor: product.available_quantity ? 'pointer' : 'not-allowed',
                    border: isSelected ? '2px solid' : '1px solid',
                    borderColor: isSelected ? 'primary.main' : '#E5E7EB',
                    borderRadius: 4,
                    backgroundColor: '#F7F7F8',
                    boxShadow: isSelected
                      ? '0 18px 30px rgba(25,118,210,0.14)'
                      : '0 8px 20px rgba(15,23,42,0.05)',
                    opacity: product.available_quantity ? 1 : 0.6,
                    overflow: 'hidden',
                    transition: 'transform .16s ease, box-shadow .16s ease',
                    '&:hover': {
                      transform: product.available_quantity ? 'translateY(-3px)' : 'none',
                      boxShadow: product.available_quantity
                        ? '0 16px 28px rgba(15,23,42,0.10)'
                        : '0 8px 20px rgba(15,23,42,0.05)',
                    },
                  }}
                >
                  {hasImage ? (
                    <CardMedia
                      component="img"
                      image={product.imageUrl}
                      alt={product.label}
                      sx={{
                        width: 'calc(100% - 28px)',
                        height: 145,
                        mx: 1.75,
                        mt: 1.75,
                        objectFit: 'cover',
                        borderRadius: 3,
                        backgroundColor: '#FFFFFF',
                      }}
                      onError={(event) => {
                        event.target.onerror = null;
                        event.target.src =
                          'https://via.placeholder.com/300x160?text=Image+indisponible';
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 'calc(100% - 28px)',
                        height: 145,
                        mx: 1.75,
                        mt: 1.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#FFFFFF',
                        borderRadius: 3,
                      }}
                    >
                      <Typography color="text.secondary" variant="body2">
                        Image indisponible
                      </Typography>
                    </Box>
                  )}
                  {isSelected ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 18,
                        top: 128,
                        minWidth: 28,
                        height: 28,
                        px: 1,
                        borderRadius: 999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#FFFFFF',
                        color: 'primary.main',
                        boxShadow: '0 8px 18px rgba(15,23,42,0.12)',
                        fontWeight: 800,
                        fontSize: 13,
                        zIndex: 1,
                      }}
                    >
                      {selectedQuantity}
                    </Box>
                  ) : null}
                  <CardContent sx={{ flexGrow: 1, px: 2, pt: 1.5, pb: 0.5 }}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.25,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        color: '#111827',
                        minHeight: 44,
                      }}
                    >
                      {product.label}
                    </Typography>
                    {product.ref && (
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'none' }}>
                        Réf : {product.ref}
                      </Typography>
                    )}
                    {product.barcode && (
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'none' }}>
                        Code-barres : {product.barcode}
                      </Typography>
                    )}
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        mt: 1,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <LocalOfferOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#111827',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Prix : {Math.round(product.price_ttc || 0)} DHS
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{ minWidth: 0 }}
                      >
                          <Inventory2OutlinedIcon sx={{ fontSize: 16, color: '#4B5563' }} />
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#111827',
                            fontWeight: 700,
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          Qty : {hideAvailability ? 'En cours' : product.available_quantity}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                  <Box sx={{ px: 2, pb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                      {isSelected
                        ? `Ajoute au resume commande : ${selectedQuantity}`
                        : 'Cliquez pour ajouter au resume commande'}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            );
            });
          })()}
          </Grid>
          {/* Infinite scroll: no pagination UI; user scrolls to load more */}
        </>
      ) : (
        <Typography>Aucun produit disponible</Typography>
      )}

        </Grid>
        <Grid
          item
          xs={12}
          lg={4}
          ref={summaryColumnRef}
          sx={{
            alignSelf: 'flex-start',
            minHeight: {
              lg: isSummaryPinned && summaryPanelMetrics.height
                ? `${summaryPanelMetrics.height}px`
                : 'auto',
            },
          }}
        >
          <Box
            sx={{
              position: {
                xs: 'static',
                lg: isSummaryPinned ? 'fixed' : 'static',
              },
              top: { lg: isSummaryPinned ? 24 : 'auto' },
              zIndex: { lg: 10 },
              width: {
                lg: isSummaryPinned && summaryPanelMetrics.width
                  ? `${summaryPanelMetrics.width}px`
                  : 'auto',
              },
            }}
          >
            <Card
              ref={summaryCardRef}
              sx={{
                borderRadius: 3,
                boxShadow: '0 18px 36px rgba(15,23,42,0.08)',
                maxHeight: 'calc(100vh - 48px)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
            <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Stack spacing={2.25}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Resume commande
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Les produits ajoutes apparaissent ici.
                    </Typography>
                  </Box>
                  <Badge badgeContent={totalCartQuantity} color="primary" showZero>
                    <ShoppingCartIcon color="action" />
                  </Badge>
                </Stack>
                <Divider />
                {cartDisplayItems.length ? (
                  <List disablePadding sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                    {cartDisplayItems.map(
                      (
                        {
                          product,
                          quantity,
                          isGiftLine,
                          unitPrice,
                          key,
                          canToggleGift,
                          giftToggleChecked,
                        },
                        index
                      ) => (
                        <ListItem
                          key={key}
                          alignItems="flex-start"
                          disableGutters
                          sx={{
                            py: 1.25,
                            borderBottom:
                              index !== cartDisplayItems.length - 1
                                ? '1px solid'
                                : 'none',
                            borderColor: 'divider',
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography sx={{ fontWeight: 700 }}>
                                {product.label}
                              </Typography>
                            }
                            secondary={
                              <Box component="span">
                                <Typography component="span" variant="body2" color="text.secondary">
                                  {isGiftLine
                                    ? 'Supplement gratuit'
                                    : `Prix : ${Math.round(unitPrice || product.price_ttc || 0)} DHS`}
                                </Typography>
                                <br />
                                <Typography component="span" variant="body2" color="text.secondary">
                                  {hideAvailability
                                    ? 'Disponibilite : inventaire en cours'
                                    : `Disponibilite : ${product.available_quantity}`}
                                </Typography>
                                {product.isComponent ? (
                                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={
                                            giftToggleChecked ??
                                            Boolean(selectedLines[product.id]?.isGift)
                                          }
                                          onChange={(event) =>
                                            handleToggleGift(
                                              product.id,
                                              event.target.checked
                                            )
                                          }
                                          color="secondary"
                                          disabled={!canToggleGift}
                                        />
                                      }
                                      label="Gratuit"
                                    />
                                    {selectedLines[product.id]?.isGift ? (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleEnsurePaidLine(product.id)}
                                      >
                                        Ajouter payant
                                      </Button>
                                    ) : null}
                                  </Stack>
                                ) : null}
                              </Box>
                            }
                          />
                          <Stack spacing={1} alignItems="flex-end" sx={{ minWidth: 136 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  isGiftLine
                                    ? handleDecreaseGiftQuantity(product.id)
                                    : handleDecreaseQuantity(product.id)
                                }
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <TextField
                                type="number"
                                size="small"
                                value={quantity}
                                onChange={(event) =>
                                  isGiftLine
                                    ? handleGiftQuantityChange(
                                        product.id,
                                        event.target.value
                                      )
                                    : handleQuantityChange(
                                        product.id,
                                        event.target.value
                                      )
                                }
                                inputProps={{
                                  min: isGiftLine ? 1 : selectedLines[product.id]?.isGift ? 0 : 1,
                                  max: product.available_quantity || undefined,
                                  style: { textAlign: 'center', width: 56 },
                                }}
                              />
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  isGiftLine
                                    ? handleIncreaseGiftQuantity(product.id)
                                    : handleIncreaseQuantity(product.id)
                                }
                                disabled={
                                  product.available_quantity &&
                                  (Number(quantity || 0) +
                                    (isGiftLine
                                      ? Number(selectedLines[product.id]?.quantity || 0)
                                      : Number(selectedLines[product.id]?.giftQuantity || 0))) >=
                                    product.available_quantity
                                }
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                            {!isGiftLine ? (
                              <TextField
                                label="Prix"
                                type="number"
                                size="small"
                                value={
                                  Number.isFinite(Number(unitPrice))
                                    ? unitPrice
                                    : Math.round(product.price_ttc || 0)
                                }
                                onChange={(event) =>
                                  handleUnitPriceChange(
                                    product.id,
                                    event.target.value
                                  )
                                }
                                inputProps={{ min: 0, style: { width: 88 } }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                0 DHS
                              </Typography>
                            )}
                          </Stack>
                        </ListItem>
                      )
                    )}
                  </List>
                ) : (
                  <Typography color="text.secondary">
                    Aucun produit ajoute pour le moment.
                  </Typography>
                )}
                <Divider />
                <Stack spacing={0.75}>
                  <Typography variant="subtitle1" textAlign="right" sx={{ fontWeight: 900 }}>
                    Total : {cartTotal} DHS
                  </Typography>
                  <Typography
                    variant="body2"
                    textAlign="right"
                    color="text.secondary"
                  >
                    Supplements gratuits : {giftCartQuantity} | Vendus : {soldCartQuantity}
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleCreateOrder}
                  disabled={!cartDisplayItems.length}
                >
                  Creer la commande
                </Button>
              </Stack>
            </CardContent>
          </Card>
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={transformDialogOpen}
        onClose={handleCloseTransformDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Transformer un produit</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ pt: 1 }}>
            <Grid item xs={12} md={7}>
              <Stack spacing={3}>
                <Typography variant="body2" color="text.secondary">
                  Sélectionnez le produit final et les composants à consommer
                  pour {transformStoreDisplay}.
                </Typography>
                <Autocomplete
                  options={finalProductOptions}
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={transformFinalProductOption}
                  onChange={(_, newValue) => setTransformFinalProductOption(newValue)}
                  disabled={transformLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Produit final"
                      placeholder="Choisissez le produit final"
                    />
                  )}
                />
                <TextField
                  type="number"
                  label="Quantité produite"
                  value={transformFinalQuantity}
                  onChange={handleTransformFinalQuantityInput}
                  inputProps={{
                    min: 1,
                  }}
                  disabled={transformLoading}
                />
                <Box>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle1">Composants</Typography>
                    {hasTransformComponents && (
                      <Typography variant="body2" color="text.secondary">
                        {transformSelectedComponentsCount} sélectionné
                        {transformSelectedComponentsCount > 1 ? 's' : ''} •{' '}
                        {transformComponentsTotalQuantity}{' '}
                        unité{transformComponentsTotalQuantity > 1 ? 's' : ''}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Cochez les composants à consommer. Le stock affiché
                    correspond au magasin sélectionné.
                  </Typography>
                  {componentProducts.length ? (
                    <Box sx={{ maxHeight: 360, overflowY: 'auto', mt: 2 }}>
                      <TextField
                        size="small"
                        placeholder="Rechercher un composant par nom, réf ou code-barres"
                        value={transformComponentFilter}
                        onChange={(e) => setTransformComponentFilter(e.target.value)}
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                      <List disablePadding>
                        {(() => {
                          const filter = (transformComponentFilter || '').trim().toLowerCase();
                          const list = filter
                            ? componentProducts.filter((c) => {
                                const label = String(c.label || '').toLowerCase();
                                const ref = String(c.ref || '').toLowerCase();
                                const barcode = String(c.barcode || '').toLowerCase();
                                return (
                                  label.includes(filter) ||
                                  ref.includes(filter) ||
                                  barcode.includes(filter)
                                );
                              })
                            : componentProducts;

                          if (!list.length) {
                            return (
                              <ListItem>
                                <ListItemText primary="Aucun composant correspondant" />
                              </ListItem>
                            );
                          }

                          return list.map((component, index) => {
                          const isSelected = Boolean(
                            transformComponentSelections[component.id]
                          );
                          const quantity =
                            transformComponentSelections[component.id]?.quantity ||
                            1;

                          return (
                            <ListItem
                              key={component.id}
                              disableGutters
                              alignItems="center"
                              sx={{
                                py: 1.5,
                                borderBottom:
                                  index !== componentProducts.length - 1
                                    ? '1px solid'
                                    : 'none',
                                borderColor: 'divider',
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                <Checkbox
                                  edge="start"
                                  checked={isSelected}
                                  onChange={(event) =>
                                    handleToggleTransformComponent(
                                      component,
                                      event.target.checked
                                    )
                                  }
                                  disabled={
                                    transformLoading ||
                                    !component.available_quantity
                                  }
                                />
                              </ListItemIcon>
                              <ListItemAvatar>
                                <Avatar
                                  variant="rounded"
                                  src={component.imageUrl || undefined}
                                  alt={component.label}
                                >
                                  {component.label?.[0] || '?'}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={component.label}
                                secondary={
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {component.ref ? `Réf : ${component.ref}` : ''}
                                    {component.ref && component.barcode ? ' • ' : ''}
                                    {component.barcode
                                      ? `Code-barres : ${component.barcode}`
                                      : ''}
                                    {(component.ref || component.barcode) && ' • '}
                                    Stock : {component.available_quantity}
                                  </Typography>
                                }
                              />
                              {isSelected && (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  sx={{ ml: { xs: 1, sm: 2 } }}
                                >
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                      handleDecreaseTransformComponentQuantity(
                                        component.id
                                      )
                                    }
                                    disabled={transformLoading || quantity <= 1}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={quantity}
                                    onChange={(event) =>
                                      handleTransformComponentQuantityChange(
                                        component.id,
                                        event.target.value
                                      )
                                    }
                                    inputProps={{
                                      min: 1,
                                      max:
                                        component.available_quantity || undefined,
                                      style: { textAlign: 'center', width: 72 },
                                    }}
                                    disabled={transformLoading}
                                  />
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                      handleIncreaseTransformComponentQuantity(
                                        component.id
                                      )
                                    }
                                    disabled={
                                      transformLoading ||
                                      (component.available_quantity &&
                                        quantity >= component.available_quantity)
                                    }
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              )}
                            </ListItem>
                          );
                          });
                        })()}
                      </List>
                    </Box>
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Aucun composant disponible dans {transformStoreDisplay}.
                    </Alert>
                  )}
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="h6">Récapitulatif</Typography>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2">Produit final</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {transformFinalProductOption?.label ||
                          'Aucun produit sélectionné'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Quantité : {transformFinalQuantity || 0}
                      </Typography>
                    </Stack>
                    <Divider />
                    <Stack spacing={1.5}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="subtitle2">Composants</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {transformSelectedComponentsCount} élément
                          {transformSelectedComponentsCount > 1 ? 's' : ''}
                        </Typography>
                      </Stack>
                      {transformBillingItems.length ? (
                        <List dense disablePadding>
                          {transformBillingItems.map((item, index) => (
                            <ListItem
                              key={item.id || index}
                              disableGutters
                              sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                            >
                              <ListItemText
                                primary={item.label || 'Composant'}
                                secondary={
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {item.ref ? `Réf : ${item.ref}` : ''}
                                    {item.ref && item.barcode ? ' • ' : ''}
                                    {item.barcode ? `Code-barres : ${item.barcode}` : ''}
                                    {(item.ref || item.barcode) && ' • '}
                                    Qté : {item.quantity}
                                  </Typography>
                                }
                              />
                              <Stack spacing={0.25} alignItems="flex-end" sx={{ minWidth: 120 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {formatCurrency(item.unitPrice)}
                                </Typography>
                                <Typography variant="subtitle2">
                                  {formatCurrency(item.total)}
                                </Typography>
                              </Stack>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Aucun composant sélectionné.
                        </Typography>
                      )}
                    </Stack>
                    <Divider />
                    <Stack spacing={0.75}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="subtitle1">Total composants</Typography>
                        <Typography variant="subtitle1">
                          {formatCurrency(transformBillingSubtotal)}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="body2" color="text.secondary">
                          Coût par unité produite
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {transformFinalQuantityNumber
                            ? formatCurrency(transformUnitCost)
                            : '—'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransformDialog} disabled={transformLoading}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitTransform}
            disabled={!isTransformFormValid || transformLoading}
          >
            {transformLoading ? 'En cours…' : 'Valider la transformation'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cartOpen} onClose={() => setCartOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Panier</DialogTitle>
        <DialogContent>
          {cartDisplayItems.length ? (
            <Stack spacing={3} sx={{ pt: 1 }}>
              <List disablePadding>
                {cartDisplayItems.map(
                  (
                    {
                      product,
                      quantity,
                      isGiftLine,
                      unitPrice,
                      key,
                      canToggleGift,
                      giftToggleChecked,
                    },
                    index
                  ) => (
                    <ListItem
                      key={key}
                      alignItems="center"
                      disableGutters
                      sx={{
                        py: 1.5,
                        borderBottom:
                          index !== cartDisplayItems.length - 1
                            ? '1px solid'
                            : 'none',
                        borderColor: 'divider',
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          variant="rounded"
                          src={product.imageUrl || undefined}
                          alt={product.label}
                        >
                          {product.label?.[0] || '?'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={product.label}
                        secondary={
                          <Box component="span">
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                            >
                              Prix : {Math.round(product.price_ttc || 0)} DHS
                            </Typography>
                            <br />
                            {hideAvailability ? (
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.secondary"
                              >
                                Inventaire en cours
                              </Typography>
                            ) : (
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.secondary"
                              >
                                Disponible : {product.available_quantity}
                              </Typography>
                            )}
                            {product.isComponent ? (
                              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={
                                        giftToggleChecked ??
                                        Boolean(selectedLines[product.id]?.isGift)
                                      }
                                      onChange={(event) =>
                                        handleToggleGift(
                                          product.id,
                                          event.target.checked
                                        )
                                      }
                                      color="secondary"
                                      disabled={!canToggleGift}
                                    />
                                  }
                                  label="Supplément Gratuit"
                                />
                                {selectedLines[product.id]?.isGift ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleEnsurePaidLine(product.id)}
                                  >
                                    Ajouter un autre payant
                                  </Button>
                                ) : null}
                              </Stack>
                            ) : null}
                          </Box>
                        }
                      />
                      <Stack spacing={1.5} alignItems="flex-end">
                        {isGiftLine ? (
                          <Stack
                            spacing={0.75}
                            alignItems="flex-end"
                            sx={{ width: '100%' }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Quantité cadeau
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  handleDecreaseGiftQuantity(product.id)
                                }
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <TextField
                                type="number"
                                size="small"
                                value={quantity || 0}
                                onChange={(event) =>
                                  handleGiftQuantityChange(
                                    product.id,
                                    event.target.value
                                  )
                                }
                                inputProps={{
                                  min: 1,
                                  max: product.available_quantity || undefined,
                                  style: { textAlign: 'center', width: 64 },
                                }}
                              />
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  handleIncreaseGiftQuantity(product.id)
                                }
                                disabled={
                                  product.available_quantity &&
                                  (quantity || 0) +
                                    (selectedLines[product.id]?.quantity || 0) >=
                                    product.available_quantity
                                }
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              Montant : 0 DHS
                            </Typography>
                          </Stack>
                        ) : (
                          <Stack spacing={0.75} alignItems="flex-end">
                            <Typography variant="body2" color="text.secondary">
                              Quantité payante
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleDecreaseQuantity(product.id)}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <TextField
                                type="number"
                                size="small"
                                value={quantity}
                                onChange={(event) =>
                                  handleQuantityChange(
                                    product.id,
                                    event.target.value
                                  )
                                }
                                inputProps={{
                                  min: selectedLines[product.id]?.isGift ? 0 : 1,
                                  max: product.available_quantity || undefined,
                                  style: { textAlign: 'center', width: 64 },
                                }}
                              />
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleIncreaseQuantity(product.id)}
                                disabled={
                                  product.available_quantity &&
                                  (quantity || 0) +
                                    (selectedLines[product.id]?.giftQuantity ||
                                      0) >= product.available_quantity
                                }
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                            <TextField
                              label="Prix unitaire"
                              type="number"
                              size="small"
                              value={
                                Number.isFinite(Number(unitPrice))
                                  ? unitPrice
                                  : Math.round(product.price_ttc || 0)
                              }
                              onChange={(event) =>
                                handleUnitPriceChange(
                                  product.id,
                                  event.target.value
                                )
                              }
                              inputProps={{ min: 0, style: { width: 120 } }}
                            />
                          </Stack>
                        )}
                      </Stack>
                    </ListItem>
                  )
                )}
              </List>
              <Typography variant="subtitle1" textAlign="right">
                Total : {cartTotal} DHS
              </Typography>
              <Typography
                variant="body2"
                textAlign="right"
                color="text.secondary"
              >
                Suppléments gratuits : {giftCartQuantity} | Vendus :{' '}
                {soldCartQuantity}
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="sales-mode-label">Mode de vente</InputLabel>
                <MUISelect
                  labelId="sales-mode-label"
                  label="Mode de vente"
                  value={salesMode}
                  onChange={(event) => setSalesMode(event.target.value)}
                >
                  {['Client', 'Glovo', 'Bringo', 'Glovo Carrefour', 'Borne'].map(
                    (mode) => (
                      <MenuItem key={mode} value={mode}>
                        {mode}
                      </MenuItem>
                    )
                  )}
                </MUISelect>
              </FormControl>
            </Stack>
          ) : (
            <Typography color="text.secondary">
              Votre panier est vide.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCartOpen(false)}>Fermer</Button>
          <Button
            variant="contained"
            onClick={handleCreateOrder}
            disabled={!cartDisplayItems.length}
          >
            Créer la commande
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StockUserHandle;
